/**
 * FoodPulseTour — Mobile onboarding / explore flow.
 *
 * Route: /food-pulse/tour  (accessible any time)
 * Route: /food-pulse/tour?first=1  (auto-shown on first mobile visit)
 *
 * Design brief: mobile-first onboarding carousel matching the 12-screen
 * design set delivered by the user. Built here on the web-app first so
 * copy, flow, TTS read-aloud and analytics can be validated end-to-end
 * before handing the spec over to the native mobile team.
 *
 * ── Card types ──
 *   splash       — logo hero + "Explore" / "Let Me In" CTAs
 *   image        — hero photo + green title + subhead + paragraph
 *   image-final  — same as image but only Previous+Skip (last card)
 *   bullets      — no photo, title + intro line + 5 bullets
 *   scroll       — long stack of info cards, single Next/Skip footer
 *
 * ── Read-aloud ──
 *   Uses window.speechSynthesis for v1 (free, offline, respects
 *   user's currently-selected UI language via i18n.language). When
 *   the Emergent LLM budget resets we can swap the click handler
 *   to the higher-quality OpenAI TTS endpoint without changing UX.
 */
import { useEffect, useMemo, useState, useCallback } from "react";
import { Link, useNavigate, useLocation } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Volume2, VolumeX, Sparkles, X } from "lucide-react";
import SeoHead from "@/components/SeoHead";

// ─────────────────────────────────────────────────────────────
// Palette — matches the delivered mockups.
// ─────────────────────────────────────────────────────────────
const P = {
  pastelBg:    "#D6EDA0",   // main onboarding bg (light lime)
  vibrantBg:   "#8BC53C",   // splash-vibrant variant (unused in v1)
  cardBg:      "#C7E38F",   // info-card tint on scroll layouts
  titleGreen:  "#8DBA2A",   // headline green
  subheadInk:  "#0A0A0A",   // near-black body text
  brandPurple: "#6D2AB1",   // Food Pulse wordmark purple
  socialBlue:  "#3B82F6",   // socialpulse.com link
};

// ─────────────────────────────────────────────────────────────
// Brand assets — sourced from the user-provided artifact library.
// Round mark used in the tour's top bar; wordmark for the splash
// title-treatment. Stock illustrations are the same set the mobile
// team is using so web and native stay visually aligned.
// ─────────────────────────────────────────────────────────────
const LOGO_ROUND    = "/foodpulse/fp-logo-round.jpg";
const LOGO_ROUND_NAME = "/foodpulse/fp-logo-round-name.jpg";

// Hero images — served from the user's asset library. Each entry
// matches its onboarding-card copy 1:1 with the delivered mockups.
const IMG = {
  familyShopping:  "https://customer-assets-eiarnc6j.emergentagent.net/job_0f76a43a-d72f-455a-b419-71d333e1ae3b/artifacts/1nj8xzlm_discover-food-locally.webp",
  coupleKitchen:   "https://customer-assets-eiarnc6j.emergentagent.net/job_0f76a43a-d72f-455a-b419-71d333e1ae3b/artifacts/yiehlhsh_Manage-food-at-home.webp",
  produceMarket:   "https://customer-assets-eiarnc6j.emergentagent.net/job_0f76a43a-d72f-455a-b419-71d333e1ae3b/artifacts/i4c1w06s_Find-Local-Deals.webp",
  foodDonation:    "https://customer-assets-eiarnc6j.emergentagent.net/job_0f76a43a-d72f-455a-b419-71d333e1ae3b/artifacts/1tebhz66_donate-excess-food.webp",
  foodWaste:       "https://customer-assets-eiarnc6j.emergentagent.net/job_0f76a43a-d72f-455a-b419-71d333e1ae3b/artifacts/rhmwd20m_reducing-food-waste.webp",
};

// ─────────────────────────────────────────────────────────────
// The onboarding flow — order confirmed with the product owner:
//
//   1. Splash (vibrant green — logo hero + Explore / Let Me In)
//   2. Pillars overview scroll (5 pillars of Food Pulse)
//   3. How To Use — part 1 (Wish / Shopping / Pantry-Stock)
//   4. How To Use — part 2 (Donation / Share Deal / Share Recipe)
//   5. Discover Food Locally      (image)
//   6. Donate Excess Food         (image, with read-aloud)
//   7. Find Local Deals           (image)
//   8. Manage Food at Home        (image)
//   9. Reducing Food Waste is Our Mission (image)
//  10. Food Pulse Ambassadors     (bullets, closes the tour)
//
// Keep this file's data section as the single source of truth —
// the mobile team can pull it as JSON if wanted.
// ─────────────────────────────────────────────────────────────
const CARDS = [
  { id: "splash", type: "splash" },

  {
    id: "pillars",
    type: "scroll",
    heading: null,
    sections: [
      {
        title: "Social Food Discovery",
        subhead: "Connect, Discover, and Eat Smarter Together",
        body:
          "Explore what your community is eating, cooking, and sharing. Follow friends, discover trending dishes, and uncover local favorites—all in one place.",
      },
      {
        title: "Manage Food at Home",
        subhead: "Smart Pantry. Zero Waste. Full Control.",
        body:
          "Track what you have in your kitchen, get reminders before items expire, and organize your fridge, pantry, and storeroom efficiently—with location-based photos and alerts.",
      },
      {
        title: "Discover Local Food Deals",
        subhead: "Real-Time Deals From Your Neighborhood Stores",
        body:
          "Save more every day by finding fresh deals on groceries, perishables, and essentials from nearby vendors. Personalized alerts ensure you never miss a bargain again.",
      },
      {
        title: "Share Food with Friends & Charities",
        subhead: "Food is Better When Shared",
        body:
          "Easily donate surplus food or share it with friends, neighbors, or local charities. Empower your community and help those in need with just a few taps.",
      },
      {
        title: "Reduce Food Waste",
        subhead: "Your Daily Actions Can Change the World",
        body:
          "Join the movement to save perfectly edible food from being wasted. From smart shopping to responsible sharing, Food Pulse helps you build good karma—and a sustainable future.",
      },
    ],
  },

  {
    id: "how-to-1",
    type: "scroll",
    heading: "How To Use Foodpulse App",
    sections: [
      {
        title: "Wish List",
        subhead: "Get the best prices for what you really need.",
        body:
          "Your grocery routine, your way. Whether you scan a barcode or add it manually, create a smart Wish List of your favorite items. Food Pulse will match them with real-time local deals, helping you save money and shop mindfully every day.",
      },
      {
        title: "Shopping List",
        subhead: "Shop smarter together.",
        body:
          "Build your Shopping List and share it instantly with family, friends, or staff. Whether you're coordinating household needs or business supplies, Food Pulse helps everyone stay on the same page—making sure nothing is forgotten and every dollar counts.",
      },
      {
        title: "Pantry/Stock List",
        subhead: "Waste less, save more–right from your kitchen.",
        body:
          "Manage everything from your fridge, pantry, or storeroom with ease. Track location, expiration dates, and receive smart 'use before' alerts with optional photos. Your food deserves better than being forgotten—use it wisely and reduce waste effortlessly.",
      },
    ],
  },

  {
    id: "how-to-2",
    type: "scroll",
    heading: "How To Use Foodpulse App",
    sections: [
      {
        title: "Donation List",
        subhead: "Give what you can. Share what you don't need.",
        body:
          "Turn your surplus into someone else's necessity. List extra or unused items in your Donation List and offer them to charities, neighbors, or community fridges—all through the Food Pulse app. It's a simple act of kindness with powerful impact.",
      },
      {
        title: "Share a Deal",
        subhead: "Be the reason someone else saves.",
        body:
          "See a great deal on food? Snap it, share it, spread the savings. Food Pulse lets you pass along bargains to your friends, family, and local groups—and even encourage your favorite stores to post their deals directly to you and your community.",
      },
      {
        title: "Share a Recipe",
        subhead: "Share your delicious recipe to your neighbors.",
        body:
          "Great meals are meant to be shared. Post your family recipes, cooking tips, and traditional dishes to inspire neighbors — and discover what your community is cooking tonight. Every recipe you share builds culinary community and cultural connection.",
      },
    ],
  },

  {
    id: "discover-food-locally",
    type: "image",
    image: IMG.familyShopping,
    imageAlt: "Family shopping fresh produce together",
    title: "Discover Food Locally",
    subhead: "Personalize your pantry with local power.",
    body:
      "Build a Wish List, and we'll help you match it with real-time availability and best prices from your favorite stores. Food Pulse empowers you to shop locally, support businesses, and stay fully stocked with what you love—at the right price.",
  },

  {
    id: "donate-excess-food",
    type: "image",
    image: IMG.foodDonation,
    imageAlt: "Hands passing a food donation box",
    title: "Donate Excess Food",
    subhead: "Where surplus meets need—in real time.",
    body:
      "Supermarkets, grocers, restaurants, and caterers can now connect instantly with local food banks and soup kitchens. Use Food Pulse to share real-time updates of edible surplus, reduce disposal costs, and nourish those in need instead of the landfill.",
  },

  {
    id: "find-local-deals",
    type: "image",
    image: IMG.produceMarket,
    imageAlt: "Colourful supermarket produce section with price signs",
    title: "Find Local Deals",
    subhead: "Never miss a markdown again.",
    body:
      "Grocers frequently cut prices to move perishables—Food Pulse makes sure you're the first to know. Get timely alerts on discounts, price drops, and clearance sales nearby. Save money, fight waste, and shop sustainably every day.",
  },

  {
    id: "manage-food-at-home",
    type: "image",
    image: IMG.coupleKitchen,
    imageAlt: "Couple prepping food in a home kitchen",
    title: "Manage Food at Home",
    subhead: "Your kitchen, optimized for less waste and more care.",
    body:
      "Plan, shop, and manage your food with precision. From shared lists to special diet alerts, Food Pulse helps you avoid duplicates, track what's already at home, and support health-focused households with various recipes and tips—all while reducing unnecessary food waste.",
  },

  {
    id: "reduce-food-waste",
    type: "image",
    image: IMG.foodWaste,
    imageAlt: "Scraping edible pasta into a trash bin — a scene we want to prevent",
    title: "Reducing Food Waste is Our Mission",
    subhead: "40% of food is wasted—but together, we can change that.",
    body:
      "Every year, $165 billion worth of edible food goes to waste in the U.S. alone. At Food Pulse, we're building a connected, conscious community that reduces food waste from farms to fridges. Join us in this powerful mission. Build Good Karma, create Social Capital, and help feed a better future.",
  },

  {
    id: "ambassadors",
    type: "bullets",
    title: "Food Pulse Ambassadors",
    intro: "As a Food Pulse Ambassador, you will:",
    bullets: [
      "Set up and grow community Food Pulse units",
      "Help local vendors join and manage their store on Food Pulse",
      "Coordinate food donations, pickups, and deliveries to families and charities",
      "Receive free food items or offers from vendors",
      "Earn social recognition and build your community impact",
    ],
  },
];

const TOTAL_STEPS = CARDS.length;

// ─────────────────────────────────────────────────────────────
// Small helpers
// ─────────────────────────────────────────────────────────────
function speakText(text, lang) {
  try {
    if (!("speechSynthesis" in window)) return null;
    window.speechSynthesis.cancel();
    const u = new SpeechSynthesisUtterance(text);
    if (lang) u.lang = lang;
    u.rate = 1.0;
    u.pitch = 1.0;
    window.speechSynthesis.speak(u);
    return u;
  } catch { return null; }
}
function stopSpeaking() {
  try { if ("speechSynthesis" in window) window.speechSynthesis.cancel(); } catch {}
}

// ─────────────────────────────────────────────────────────────
// Splash — final design per the delivered mockups.
// Kelly-green background, white typography, halo'd brand mark
// centred, feature bullets and CTAs sit directly on the green
// (no pastel card), with the "© Food Pulse 2025" footer anchored
// to the bottom safe-area.
// ─────────────────────────────────────────────────────────────
function Splash({ onExplore, onLetMeIn }) {
  const year = new Date().getFullYear();
  return (
    <div
      className="min-h-[100svh] w-full flex flex-col px-6 pt-14 pb-6"
      style={{
        background: P.vibrantBg,
        fontFamily:
          "'Manrope', 'Poppins', 'Inter', -apple-system, BlinkMacSystemFont, 'Segoe UI', sans-serif",
      }}
      data-testid="fp-tour-splash"
    >
      {/* Wordmark ───────────────────────────────── */}
      <div className="text-center">
        <div className="text-[42px] leading-none font-extrabold tracking-tight text-white">
          Food Pulse<sup className="text-lg font-semibold align-super ml-1">™</sup>
        </div>
      </div>

      {/* Halo'd brand mark ─────────────────────── */}
      <div className="mt-8 flex items-center justify-center">
        <div className="w-60 h-60 rounded-full grid place-items-center"
             style={{ background: "rgba(255,255,255,0.16)" }}>
          <div className="w-48 h-48 rounded-full grid place-items-center"
               style={{ background: "rgba(255,255,255,0.24)" }}>
            <div className="w-40 h-40 rounded-full grid place-items-center bg-white shadow-lg overflow-hidden">
              <img src={LOGO_ROUND} alt="Food Pulse logo"
                   className="w-full h-full object-cover rounded-full" />
            </div>
          </div>
        </div>
      </div>

      {/* Tagline + feature bullets ─────────────── */}
      <div className="mt-8 text-center text-white"
           style={{ fontFamily: "inherit" }}>
        <h1 className="text-[30px] font-extrabold tracking-tight leading-tight"
            style={{ fontFamily: "inherit" }}>
          Social Food Discovery
        </h1>
        <ul className="mt-4 text-[17px] font-semibold leading-[1.9] space-y-0.5">
          <li>• Manage Food at Home</li>
          <li>• Discover Local Food Deals</li>
          <li>• Share Food with Friends &amp; Charities</li>
          <li>• Reduce Food Waste</li>
        </ul>
        <div className="mt-4 text-[15px] font-semibold">
          Powered by{" "}
          <a href="https://socialpulse.com"
             className="underline decoration-2 underline-offset-2"
             style={{ color: P.socialBlue }}
             data-testid="fp-tour-socialpulse-link">
            socialpulse.com
          </a>
          <span className="ml-1 text-[13px]">(R)</span>
        </div>
      </div>

      {/* CTA pills ─────────────────────────────── */}
      <div className="mt-5 flex gap-4 justify-center">
        <button
          type="button"
          onClick={onExplore}
          className="min-w-[130px] rounded-full bg-white px-8 py-3 text-[17px] font-bold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
          style={{ color: "#111" }}
          data-testid="fp-tour-explore-btn"
        >
          Explore
        </button>
        <button
          type="button"
          onClick={onLetMeIn}
          className="min-w-[130px] rounded-full bg-white px-8 py-3 text-[17px] font-bold shadow-md hover:shadow-lg transition-all hover:-translate-y-0.5"
          style={{ color: "#111" }}
          data-testid="fp-tour-let-me-in-btn"
        >
          Let Me In
        </button>
      </div>

      {/* Copyright footer — pinned to the bottom */}
      <div className="mt-auto pt-6 text-center text-white text-[13px] font-medium opacity-90">
        © Food Pulse {year}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// TopBar — Food Pulse logo strip at the top of every non-splash
// card. Slim, consistent, aligns with the mockups.
// ─────────────────────────────────────────────────────────────
function TopBar() {
  return (
    <div className="pt-6 pb-4 flex items-center justify-center gap-2">
      <img src={LOGO_ROUND} alt="Food Pulse logo"
           className="w-10 h-10 rounded-full object-cover" />
      <div className="text-2xl font-bold" style={{ color: P.brandPurple }}>
        Food Pulse<sup className="text-xs">™</sup>
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Card renderers
// ─────────────────────────────────────────────────────────────
function ImageCard({ card, onSpeak, speaking }) {
  return (
    <div className="px-5" data-testid={`fp-tour-card-${card.id}`}>
      <div className="relative rounded-2xl overflow-hidden shadow-sm">
        <img src={card.image} alt={card.imageAlt} className="w-full h-56 sm:h-64 object-cover"
             loading="lazy" />
        {/* Speaker toggle — sits on the left edge of the image just
            like the "Donate Excess Food" mockup. */}
        <button
          type="button"
          onClick={onSpeak}
          className="absolute top-3 left-0 rounded-r-full py-2 pl-2 pr-3 shadow"
          style={{ background: speaking ? P.titleGreen : "#F2FBD9" }}
          aria-label={speaking ? "Stop read-aloud" : "Read this card aloud"}
          data-testid={`fp-tour-tts-${card.id}`}
        >
          {speaking ? <VolumeX className="w-4 h-4 text-white" />
                    : <Volume2 className="w-4 h-4" style={{ color: P.titleGreen }} />}
        </button>
      </div>

      <h1 className="mt-6 text-3xl font-bold" style={{ color: P.titleGreen }}>
        {card.title}
      </h1>
      <p className="mt-3 text-lg font-semibold text-center" style={{ color: P.subheadInk }}>
        {card.subhead}
      </p>
      <p className="mt-4 text-base leading-relaxed text-center opacity-90"
         style={{ color: P.subheadInk }}>
        {card.body}
      </p>
    </div>
  );
}

function BulletsCard({ card }) {
  return (
    <div className="px-5 pt-8" data-testid={`fp-tour-card-${card.id}`}>
      <h1 className="text-4xl font-bold mb-6" style={{ color: P.titleGreen }}>
        {card.title}
      </h1>
      <p className="text-lg font-semibold mb-4" style={{ color: P.subheadInk }}>
        {card.intro}
      </p>
      <ul className="space-y-4">
        {card.bullets.map((b, i) => (
          <li key={i} className="text-base leading-relaxed" style={{ color: P.subheadInk }}>
            {b}
          </li>
        ))}
      </ul>
    </div>
  );
}

function ScrollCard({ card }) {
  return (
    <div className="px-4" data-testid={`fp-tour-card-${card.id}`}>
      {card.heading && (
        <div className="rounded-2xl px-5 py-4 mb-4 font-bold text-xl"
             style={{ background: P.cardBg, color: P.subheadInk }}>
          {card.heading}
        </div>
      )}
      <div className="space-y-4">
        {card.sections.map((s, i) => (
          <div key={i} className="rounded-2xl px-5 py-5"
               style={{ background: P.cardBg }}>
            <h3 className="text-xl font-bold mb-2" style={{ color: P.subheadInk }}>
              {s.title}
            </h3>
            <p className="text-base font-semibold mb-2" style={{ color: P.subheadInk }}>
              {s.subhead}
            </p>
            <p className="text-sm leading-relaxed" style={{ color: P.subheadInk }}>
              {s.body}
            </p>
          </div>
        ))}
      </div>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// NavBar — Previous / Next / Skip pills at the bottom.
// Cards flagged with `type: "image-final"` hide the Next button.
// ─────────────────────────────────────────────────────────────
function NavBar({ isFirst, isLast, hideNext, onPrev, onNext, onSkip }) {
  return (
    <div className="mt-8 mb-6 px-5 flex items-center justify-between gap-3">
      {isFirst ? (
        <span />
      ) : (
        <button type="button" onClick={onPrev}
                className="border-2 border-black rounded-full px-6 py-3 font-semibold hover:bg-black hover:text-white transition-colors"
                data-testid="fp-tour-prev-btn">
          Previous
        </button>
      )}

      {!hideNext && (
        <button type="button" onClick={onNext}
                className="border-2 border-black rounded-full px-8 py-3 font-semibold hover:bg-black hover:text-white transition-colors"
                data-testid="fp-tour-next-btn">
          {isLast ? "Finish" : "Next"}
        </button>
      )}

      <button type="button" onClick={onSkip}
              className="border-2 border-black rounded-full px-6 py-3 font-semibold hover:bg-black hover:text-white transition-colors"
              data-testid="fp-tour-skip-btn">
        Skip
      </button>
    </div>
  );
}

// ─────────────────────────────────────────────────────────────
// Main page
// ─────────────────────────────────────────────────────────────
export default function FoodPulseTour() {
  const nav = useNavigate();
  const loc = useLocation();
  const { i18n } = useTranslation();

  // The splash counts as step 0. When we tick past it we render the
  // per-card layout with TopBar + card + NavBar.
  const [step, setStep] = useState(0);
  const [speakingId, setSpeakingId] = useState(null);

  const card = CARDS[step];

  // Mark the tour as seen so the auto-first-visit trigger doesn't
  // pester the same device again.
  useEffect(() => {
    try { localStorage.setItem("fp_tour_seen_v1", "1"); } catch {}
  }, []);

  useEffect(() => () => stopSpeaking(), []);

  const goNext = useCallback(() => {
    stopSpeaking(); setSpeakingId(null);
    if (step + 1 >= TOTAL_STEPS) {
      // End of tour — deep-link to registration with a return path.
      nav("/register?next=/food-pulse");
    } else {
      setStep(step + 1);
    }
  }, [step, nav]);

  const goPrev = useCallback(() => {
    stopSpeaking(); setSpeakingId(null);
    if (step > 0) setStep(step - 1);
  }, [step]);

  const skip = useCallback(() => {
    stopSpeaking(); setSpeakingId(null);
    nav("/food-pulse");
  }, [nav]);

  const explore = useCallback(() => {
    setStep(1);   // straight into the first content card
  }, []);

  const letMeIn = useCallback(() => {
    nav("/register?next=/food-pulse");
  }, [nav]);

  const speakActiveCard = useCallback(() => {
    if (!card) return;
    if (speakingId === card.id) { stopSpeaking(); setSpeakingId(null); return; }
    const text = [card.title, card.subhead, card.body]
      .filter(Boolean).join(". ");
    const u = speakText(text, i18n?.language || "en-US");
    if (u) {
      u.onend = () => setSpeakingId(null);
      u.onerror = () => setSpeakingId(null);
      setSpeakingId(card.id);
    }
  }, [card, speakingId, i18n]);

  const isFirstContent = step === 1;
  const isLastContent  = step === TOTAL_STEPS - 1;
  const hideNext       = false;   // no card intentionally hides Next in the current flow

  // ── Render splash ────────────────────────────────────────
  if (card?.type === "splash") {
    return (
      <>
        <SeoHead title="Welcome to Food Pulse — Social Food Discovery"
                 description="Manage food at home, discover local deals, share with friends and charities, reduce food waste." />
        <div className="w-full flex justify-center" style={{ background: P.vibrantBg }}>
          <div className="w-full max-w-md">
            <Splash onExplore={explore} onLetMeIn={letMeIn} />
          </div>
        </div>
      </>
    );
  }

  // ── Render content card ─────────────────────────────────
  return (
    <>
      <SeoHead title="Explore Food Pulse — Take the Tour" />
      <div className="min-h-[100svh] w-full flex justify-center"
           style={{ background: P.pastelBg }}>
        <div className="w-full max-w-md flex flex-col min-h-[100svh]"
             data-testid="fp-tour-container">
          <TopBar />

          {/* Close (X) — quick escape hatch back to /food-pulse. */}
          <button type="button" onClick={skip}
                  className="fixed top-3 right-3 z-40 rounded-full bg-white/70 backdrop-blur p-2 shadow"
                  aria-label="Close tour"
                  data-testid="fp-tour-close-btn">
            <X className="w-4 h-4" />
          </button>

          <div className="flex-1">
            {card.type === "image"       && <ImageCard   card={card} onSpeak={speakActiveCard} speaking={speakingId === card.id} />}
            {card.type === "bullets"     && <BulletsCard card={card} />}
            {card.type === "scroll"      && <ScrollCard  card={card} />}
          </div>

          {/* Simple progress dots so users always know where they are. */}
          <div className="mt-4 flex items-center justify-center gap-1.5">
            {CARDS.slice(1).map((_, i) => (
              <span key={i}
                    className="block h-1.5 rounded-full transition-all"
                    style={{
                      width: i === (step - 1) ? 22 : 6,
                      background: i === (step - 1) ? P.titleGreen : "rgba(0,0,0,0.25)",
                    }} />
            ))}
          </div>

          <NavBar
            isFirst={isFirstContent}
            isLast={isLastContent}
            hideNext={hideNext}
            onPrev={goPrev}
            onNext={goNext}
            onSkip={skip}
          />
        </div>
      </div>
    </>
  );
}
